"use client";

import type React from "react";
import { forwardRef } from "react";

import { cn } from "@/lib/utils";

// Opaque, surface-relative row fills. Pinned/sticky columns and the sticky
// header must be fully opaque or horizontally-scrolled cells show through them,
// and the fill must not depend on the backdrop: these grids render both
// standalone (channel surface is --background) and embedded in a session panel
// (--surface). Compositing every tint onto --surface keeps a pinned column an
// exact match for its own row on any backdrop, and keeps the record grid and
// the CSV sheet artifact consistent. Each row picks exactly one base fill;
// hover overrides via a higher-specificity variant.
export const TABLE_ROW_BACKGROUND = "[&>td]:bg-surface";
export const TABLE_ROW_STRIPE_BACKGROUND =
  "[&>td]:[background-color:color-mix(in_oklab,hsl(var(--muted))_45%,hsl(var(--surface)))]";
export const TABLE_ROW_HOVER_BACKGROUND =
  "group-hover/row:[&>td]:[background-color:color-mix(in_oklab,hsl(var(--muted))_55%,hsl(var(--border)))]";

// Sticky column: pinned to the inline start, layered above scrolling cells,
// with a 1px border shadow standing in for a right border that survives the
// sticky offset.
export const TABLE_PINNED_COLUMN =
  "sticky left-0 z-20 shadow-[1px_0_0_0_hsl(var(--border)/0.45)]";

// Sticky header treatment shared by both grids. Per-grid differences (z-index,
// text size, uppercase CSV styling) stay local to each table.
export const TABLE_STICKY_HEADER = "sticky top-0 grid bg-muted";

/**
 * The scrolling viewport for a virtualized table grid. Owns the shared
 * min-h-0/flex-1/overflow-auto/scrollbar-thin container styling; accepts
 * className for per-grid extras (e.g. bg-surface).
 */
export const TableScrollContainer = forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(function TableScrollContainer({ className, ...props }, ref) {
  return (
    <div
      ref={ref}
      className={cn("min-h-0 flex-1 overflow-auto scrollbar-thin", className)}
      {...props}
    />
  );
});
